import { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, Text, View, useWindowDimensions } from 'react-native';
import {
  Check,
  ClipboardList,
  Download,
  FileEdit,
  FileText,
  PackageCheck,
  Plus,
  Search,
  Send,
  Trash2,
  Truck,
  CheckCheck,
  X,
} from 'lucide-react-native';

import {
  Badge,
  Button,
  Card,
  EmptyState,
  SkeletonRows,
  Sheet,
  StatTile,
  Tabs,
  TextField,
  Select,
  type SelectOption,
} from '@/components/ui';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import {
  solicitudesActivasQuery,
  solicitudesAdminQuery,
  solicitudesPorArea,
  borradorPorArea,
  crearBorrador,
  editarBorrador,
  enviarBorrador,
  eliminarSolicitud,
  actualizarEstadoSolicitud,
  entregarSolicitud,
} from '@/services/firebase/solicitudes';
import { insumosInternosQuery, productosVentaQuery, upsertInsumoInterno, upsertProductoVenta } from '@/services/firebase/inventario';
import { areasQuery } from '@/services/firebase/areas';
import { exportEntregasExcel, exportEntregasPDF } from '@/services/export';
import { useAuth, selectIsAdmin, selectIsDoctor, selectIsRecepcion } from '@/stores/authStore';
import { colors, radius, typography } from '@/theme/tokens';
import { formatDateTime, normalizar } from '@/utils/format';
import { AREAS, areaNombre } from '@/utils/areas';
import type { ItemSolicitud, Solicitud, EstadoSolicitud, TipoSolicitud } from '@/types/domain';

const ESTADO_TONO: Record<EstadoSolicitud, 'magenta' | 'success' | 'warning' | 'danger' | 'neutral' | 'info'> = {
  borrador: 'neutral',
  pendiente: 'warning',
  aprobada: 'info',
  comprada: 'magenta',
  entregada: 'success',
  entregada_parcial: 'info',
  cancelada: 'neutral',
  rechazada: 'danger',
};

const ESTADO_LABEL: Record<EstadoSolicitud, string> = {
  borrador: 'BORRADOR',
  pendiente: 'PENDIENTE',
  aprobada: 'APROBADA',
  comprada: 'COMPRADA',
  entregada: 'ENTREGADA',
  entregada_parcial: 'PARCIAL',
  cancelada: 'CANCELADA',
  rechazada: 'RECHAZADA',
};

export function SolicitudesScreen() {
  const { width } = useWindowDimensions();
  const wide = width >= 920;
  const isAdmin = useAuth(selectIsAdmin);
  const isDoctor = useAuth(selectIsDoctor);
  const perfil = useAuth((s) => s.perfil);

  const isRecepcion = useAuth(selectIsRecepcion);
  if (isAdmin) return <AdminSolicitudes wide={wide} />;
  if (isDoctor) return <VistaSolicitante wide={wide} tipo="insumos" />;
  if (isRecepcion) return <VistaSolicitante wide={wide} tipo="farmacia" />;
  return (
    <ScrollView contentContainerStyle={{ padding: wide ? 28 : 16 }}>
      <Card>
        <EmptyState
          illustration="note"
          title="Sin acceso"
          message="No tenés permiso para ver solicitudes."
        />
      </Card>
    </ScrollView>
  );
}

// ============================================================================
// ADMIN — alterna entre gestionar solicitudes y pedir para un área (admin+doctora)
// ============================================================================
function AdminSolicitudes({ wide }: { wide: boolean }) {
  const [modo, setModo] = useState<'gestionar' | 'pedir'>('gestionar');
  return (
    <View style={{ flex: 1 }}>
      <View style={{ paddingHorizontal: wide ? 28 : 16, paddingTop: 16 }}>
        <Tabs
          active={modo}
          onChange={(t) => setModo(t as 'gestionar' | 'pedir')}
          items={[
            { id: 'gestionar', label: 'Gestionar', icon: ClipboardList },
            { id: 'pedir', label: 'Pedir para un área', icon: Plus },
          ]}
        />
      </View>
      {modo === 'gestionar' ? <VistaAdmin wide={wide} /> : <VistaSolicitante wide={wide} tipo="insumos" />}
    </View>
  );
}

// ============================================================================
// VISTA ADMIN — todas las solicitudes activas
// ============================================================================
function VistaAdmin({ wide }: { wide: boolean }) {
  const { data, loading } = useFirestoreCollection(solicitudesAdminQuery);
  const { data: areasData } = useFirestoreCollection(areasQuery);
  const [estadoFiltro, setEstadoFiltro] = useState<'todas' | 'pendientes' | 'proceso' | 'entregadas'>('todas');
  const [areaFiltro, setAreaFiltro] = useState<string>('todas');

  // Opciones del filtro de área: dinámicas si existen, sino el listado estático.
  const areaOpciones = useMemo<SelectOption[]>(() => {
    const list =
      areasData.length > 0
        ? areasData.map((a: any) => ({ value: a.slug, label: a.nombre }))
        : AREAS.map((a) => ({ value: a.id, label: a.nombre }));
    return [{ value: 'todas', label: 'Todas las áreas' }, ...list];
  }, [areasData]);
  const [buscar, setBuscar] = useState('');
  const [desdeStr, setDesdeStr] = useState('');
  const [hastaStr, setHastaStr] = useState('');
  const verEntregadas = estadoFiltro === 'entregadas';

  // Conteos por estado sobre TODA la data (para los cuadritos).
  const counts = useMemo(() => {
    let pend = 0,
      proc = 0,
      entr = 0;
    data.forEach((s) => {
      if (s.estado === 'pendiente') pend++;
      else if (s.estado === 'aprobada' || s.estado === 'comprada') proc++;
      else if (s.estado === 'entregada' || s.estado === 'entregada_parcial') entr++;
    });
    return { pend, proc, entr, total: data.length };
  }, [data]);

  const filtradas = useMemo(() => {
    const desde = desdeStr ? new Date(desdeStr) : null;
    const hasta = hastaStr
      ? (() => {
          const h = new Date(hastaStr);
          h.setHours(23, 59, 59, 999);
          return h;
        })()
      : null;
    const q = normalizar(buscar);
    return data.filter((s) => {
      // Estado (según el cuadrito clickeado)
      if (estadoFiltro === 'pendientes' && s.estado !== 'pendiente') return false;
      if (estadoFiltro === 'proceso' && !(s.estado === 'aprobada' || s.estado === 'comprada')) return false;
      if (estadoFiltro === 'entregadas' && !(s.estado === 'entregada' || s.estado === 'entregada_parcial')) return false;
      // Área
      if (areaFiltro !== 'todas' && s.area !== areaFiltro) return false;
      // Fecha: en Entregadas filtra por fecha de entrega; sino por creación.
      const fref = verEntregadas
        ? (s as any).entregadaEn?.toDate?.() ?? (s as any).actualizadaEn?.toDate?.() ?? s.creadaEn?.toDate?.()
        : s.creadaEn?.toDate?.();
      if (desde && fref && fref < desde) return false;
      if (hasta && fref && fref > hasta) return false;
      // Buscar por insumo
      if (q && !(s.items ?? []).some((it) => normalizar(it.insumoNombre).includes(q))) return false;
      return true;
    });
  }, [data, estadoFiltro, areaFiltro, desdeStr, hastaStr, buscar, verEntregadas]);

  const hayFiltros = areaFiltro !== 'todas' || !!desdeStr || !!hastaStr || !!buscar.trim();
  const limpiarFiltros = () => {
    setAreaFiltro('todas');
    setDesdeStr('');
    setHastaStr('');
    setBuscar('');
  };

  return (
    <ScrollView contentContainerStyle={{ padding: wide ? 28 : 16, gap: 20, paddingBottom: 80 }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        <StatTile
          label="Pendientes"
          value={String(counts.pend)}
          accent="warning"
          icon={<ClipboardList size={20} color={colors.warning[600]} />}
          hint="Para revisar"
          active={estadoFiltro === 'pendientes'}
          onPress={() => setEstadoFiltro(estadoFiltro === 'pendientes' ? 'todas' : 'pendientes')}
        />
        <StatTile
          label="En proceso"
          value={String(counts.proc)}
          accent="magenta"
          icon={<Truck size={20} color={colors.magenta[700]} />}
          hint="Aprobadas o compradas"
          active={estadoFiltro === 'proceso'}
          onPress={() => setEstadoFiltro(estadoFiltro === 'proceso' ? 'todas' : 'proceso')}
        />
        <StatTile
          label="Entregadas"
          value={String(counts.entr)}
          accent="success"
          icon={<CheckCheck size={20} color={colors.success[600]} />}
          hint="Historial de entregas"
          active={estadoFiltro === 'entregadas'}
          onPress={() => setEstadoFiltro(estadoFiltro === 'entregadas' ? 'todas' : 'entregadas')}
        />
        <StatTile
          label="Total"
          value={String(counts.total)}
          accent="neutral"
          hint="Ver todas"
          active={estadoFiltro === 'todas'}
          onPress={() => setEstadoFiltro('todas')}
        />
      </View>

      <Card>
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <View style={{ flex: 1, minWidth: 200 }}>
            <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 20, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
              {verEntregadas ? 'Historial de entregas' : 'Solicitudes de áreas'}
            </Text>
            <Text style={{ color: colors.ink[500], fontSize: 13, marginTop: 2 }}>
              {verEntregadas
                ? 'Qué se entregó, a quién, cuánto y cuándo. Tocá los cuadritos de arriba para cambiar de vista.'
                : 'Aprobá, comprá y entregá. Al entregar se descuenta del stock interno.'}
            </Text>
          </View>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Button
              label="Excel"
              variant="success"
              size="sm"
              iconLeft={<Download size={14} color={colors.white} />}
              onPress={async () => {
                try {
                  await exportEntregasExcel({ solicitudes: filtradas, desde: desdeStr ? new Date(desdeStr) : undefined, hasta: hastaStr ? new Date(hastaStr) : undefined });
                } catch (e: any) {
                  alert(e?.message ?? 'No se pudo exportar.');
                }
              }}
            />
            <Button
              label="PDF"
              variant="danger"
              size="sm"
              iconLeft={<FileText size={14} color={colors.white} />}
              onPress={async () => {
                try {
                  await exportEntregasPDF({ solicitudes: filtradas, desde: desdeStr ? new Date(desdeStr) : undefined, hasta: hastaStr ? new Date(hastaStr) : undefined });
                } catch (e: any) {
                  alert(e?.message ?? 'No se pudo exportar.');
                }
              }}
            />
          </View>
        </View>

        {/* Filtros */}
        <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
          {/* Buscar por insumo */}
          <View style={{ minWidth: 200, flex: 1, gap: 4 }}>
            <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.ink[500], letterSpacing: 0.6, textTransform: 'uppercase' }}>
              Buscar insumo
            </Text>
            {Platform.OS === 'web' ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: colors.white, borderRadius: 12, borderWidth: 1.5, borderColor: colors.ink[200], paddingHorizontal: 12, height: 50 }}>
                <Search size={16} color={colors.ink[400]} />
                {/* @ts-ignore web-only native input */}
                <input
                  type="text"
                  value={buscar}
                  onChange={(e: any) => setBuscar(e.target.value)}
                  placeholder="Ej: jeringa, suero…"
                  style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: colors.ink[900], fontFamily: 'inherit' }}
                />
              </View>
            ) : (
              <TextField value={buscar} onChangeText={setBuscar} placeholder="Buscar insumo" />
            )}
          </View>
          <View style={{ minWidth: 170 }}>
            <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.ink[500], letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 4 }}>
              Área
            </Text>
            <Select
              value={areaFiltro}
              onChange={(v) => setAreaFiltro((v as string) ?? 'todas')}
              options={areaOpciones}
            />
          </View>
          {Platform.OS === 'web' ? (
            <>
              <View style={{ gap: 4 }}>
                <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.ink[500], letterSpacing: 0.6, textTransform: 'uppercase' }}>
                  Desde
                </Text>
                {/* @ts-ignore web-only native input */}
                <input
                  type="date"
                  value={desdeStr}
                  onChange={(e: any) => setDesdeStr(e.target.value)}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 12,
                    border: `1.5px solid ${colors.ink[200]}`,
                    background: colors.white,
                    color: colors.ink[900],
                    fontSize: 14,
                    fontFamily: 'inherit',
                    outline: 'none',
                    width: 150,
                    height: 50,
                    boxSizing: 'border-box',
                  }}
                />
              </View>
              <View style={{ gap: 4 }}>
                <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.ink[500], letterSpacing: 0.6, textTransform: 'uppercase' }}>
                  Hasta
                </Text>
                {/* @ts-ignore web-only native input */}
                <input
                  type="date"
                  value={hastaStr}
                  onChange={(e: any) => setHastaStr(e.target.value)}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 12,
                    border: `1.5px solid ${colors.ink[200]}`,
                    background: colors.white,
                    color: colors.ink[900],
                    fontSize: 14,
                    fontFamily: 'inherit',
                    outline: 'none',
                    width: 150,
                    height: 50,
                    boxSizing: 'border-box',
                  }}
                />
              </View>
            </>
          ) : null}
          {hayFiltros ? (
            <Pressable
              onPress={limpiarFiltros}
              style={({ hovered }) => ({
                paddingHorizontal: 14,
                height: 50,
                borderRadius: 12,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: hovered ? colors.crema[100] : colors.crema[50],
                borderWidth: 1.5,
                borderColor: colors.ink[200],
                // @ts-ignore web only
                cursor: 'pointer',
              })}
            >
              <Text style={{ fontSize: 13, fontWeight: typography.weights.bold, color: colors.ink[700] }}>
                Limpiar
              </Text>
            </Pressable>
          ) : null}
        </View>
      </Card>

      {loading ? (
        <SkeletonRows rows={3} />
      ) : filtradas.length === 0 ? (
        <Card>
          <EmptyState
            illustration={verEntregadas ? 'note' : 'cat'}
            title={verEntregadas ? 'Sin entregas' : data.length === 0 ? 'Sin solicitudes activas' : 'Sin resultados'}
            message={
              verEntregadas
                ? 'Acá vas a ver el historial de lo que entregaste: qué, a quién, cuánto y cuándo.'
                : data.length === 0
                  ? 'Cuando las áreas envíen pedidos los verás acá.'
                  : 'No hay solicitudes con esos filtros. Probá cambiando área, fechas o el insumo.'
            }
          />
        </Card>
      ) : (
        <View style={{ gap: 8 }}>
          {filtradas.map((s) => (
            <SolicitudCard key={s.id} solicitud={s} esAdmin />
          ))}
        </View>
      )}
    </ScrollView>
  );
}

// ============================================================================
// VISTA SOLICITANTE — doctora (insumos) o recepción (farmacia)
// ============================================================================
function VistaSolicitante({ wide, tipo }: { wide: boolean; tipo: TipoSolicitud }) {
  const perfil = useAuth((s) => s.perfil);
  // Recepción siempre opera sobre área 'recepcion'. Doctora puede elegir.
  const [areaId, setAreaId] = useState<string>(tipo === 'farmacia' ? 'recepcion' : 'consultorio_1');
  const [editorOpen, setEditorOpen] = useState(false);

  const { data: areasData } = useFirestoreCollection(areasQuery);

  // Áreas que la doctora puede pedir: dinámicas (activa + pedible) o, si la
  // colección está vacía, el listado estático sin recepción/vetpraxis/otra.
  const areasPedibles = useMemo<{ id: string; nombre: string }[]>(() => {
    const dyn = areasData
      .filter((a: any) => a.activa !== false && a.pedible !== false)
      .map((a: any) => ({ id: a.slug, nombre: a.nombre }));
    if (dyn.length > 0) return dyn;
    return AREAS.filter((a) => a.id !== 'otra' && a.id !== 'vetpraxis' && a.id !== 'recepcion').map((a) => ({
      id: a.id,
      nombre: a.nombre,
    }));
  }, [areasData]);

  // Nombre legible de un área (dinámica o estática).
  const nombreDeArea = (slug: string): string =>
    areasData.find((a: any) => a.slug === slug)?.nombre ?? areaNombre(slug);

  // Si el área seleccionada dejó de estar disponible, saltamos a la primera.
  useEffect(() => {
    if (tipo !== 'insumos' || areasPedibles.length === 0) return;
    if (!areasPedibles.some((a) => a.id === areaId)) setAreaId(areasPedibles[0].id);
  }, [areasPedibles, areaId, tipo]);

  const queryArea = useMemo(() => solicitudesPorArea(areaId), [areaId]);
  const queryBorrador = useMemo(() => borradorPorArea(areaId, tipo), [areaId, tipo]);

  const { data: todasArea, loading } = useFirestoreCollection(queryArea);
  const { data: borradoresData } = useFirestoreCollection(queryBorrador);

  // Filtrar solo solicitudes del tipo que corresponde
  const todas = useMemo(
    () => todasArea.filter((s) => (s.tipo ?? 'insumos') === tipo),
    [todasArea, tipo],
  );

  const borrador = borradoresData[0] ?? null;
  const enviadas = useMemo(() => todas.filter((s) => s.estado !== 'borrador'), [todas]);
  const pendientes = enviadas.filter((s) => s.estado === 'pendiente').length;
  const enProceso = enviadas.filter((s) => s.estado === 'aprobada' || s.estado === 'comprada').length;
  const entregadas = enviadas.filter(
    (s) => s.estado === 'entregada' || s.estado === 'entregada_parcial',
  ).length;

  const onAbrirEditor = () => setEditorOpen(true);

  // "Empezar lista" ya NO crea el borrador en la base: solo abre el editor.
  // El borrador recién se crea cuando se guarda/envía con al menos un ítem.
  const onCrearBorrador = () => setEditorOpen(true);

  const onEnviar = async () => {
    if (!borrador || !perfil) return;
    if (!borrador.items || borrador.items.length === 0) {
      alert('Agregá al menos un insumo antes de enviar.');
      return;
    }
    await enviarBorrador(borrador.id, perfil.uid, perfil.nombre);
  };

  // Cancelar: si la lista quedó vacía (recién empezada, sin ítems guardados),
  // se descarta el borrador para que no quede una "lista en preparación" fantasma.
  const onCancelarEditor = async () => {
    setEditorOpen(false);
    if (borrador && borrador.estado === 'borrador' && (borrador.items?.length ?? 0) === 0) {
      try {
        await eliminarSolicitud(borrador.id);
      } catch {
        /* si falla, queda el borrador vacío; no es crítico */
      }
    }
  };

  return (
    <ScrollView contentContainerStyle={{ padding: wide ? 28 : 16, gap: 20, paddingBottom: 80 }}>
      {/* Selector de área — solo para doctora (recepción siempre pide para recepcion) */}
      {tipo === 'insumos' ? (
        <View
          style={{
            flexDirection: 'row',
            gap: 4,
            flexWrap: 'wrap',
            padding: 5,
            backgroundColor: colors.white,
            borderRadius: radius.full,
            borderWidth: 1,
            borderColor: colors.ink[100],
            alignSelf: 'flex-start',
          }}
        >
          {areasPedibles.map((a) => {
            const active = areaId === a.id;
            return (
              <Pressable
                key={a.id}
                onPress={() => setAreaId(a.id)}
                style={({ hovered }) => ({
                  paddingHorizontal: 16,
                  paddingVertical: 8,
                  borderRadius: 999,
                  backgroundColor: active ? colors.magenta[700] : hovered ? colors.crema[50] : 'transparent',
                  // @ts-ignore web only
                  cursor: 'pointer',
                })}
              >
                <Text style={{ color: active ? colors.white : colors.ink[700], fontWeight: typography.weights.bold, fontSize: 13 }}>
                  {a.nombre}
                </Text>
              </Pressable>
            );
          })}
        </View>
      ) : null}

      {/* Borrador activo */}
      <Card padding={20} style={{ gap: 14, borderWidth: 2, borderColor: borrador ? colors.warning[500] : colors.ink[100] }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <FileEdit size={20} color={colors.warning[600]} />
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
            {tipo === 'farmacia' ? 'Pedido para Farmacia' : `Lista en preparación · ${nombreDeArea(areaId)}`}
          </Text>
        </View>
        {borrador ? (
          <>
            <Text style={{ color: colors.ink[600], fontSize: 13 }}>
              {borrador.items.length} {borrador.items.length === 1 ? 'ítem' : 'ítems'} pendientes · última edición por {borrador.ultimoEditorNombre} ({formatDateTime(borrador.actualizadaEn?.toDate?.() ?? borrador.creadaEn?.toDate?.() ?? null)})
            </Text>
            {borrador.items.length > 0 ? (
              <View style={{ gap: 6 }}>
                {borrador.items.map((it, i) => (
                  <View
                    key={i}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 10,
                      borderRadius: 10,
                      backgroundColor: colors.crema[50],
                    }}
                  >
                    <Text style={{ color: colors.ink[800], fontSize: 13 }} numberOfLines={1}>
                      {it.insumoNombre}
                    </Text>
                    <Text style={{ color: colors.ink[600], fontSize: 13, fontWeight: typography.weights.bold }}>
                      ×{it.cantidad}
                    </Text>
                  </View>
                ))}
              </View>
            ) : (
              <Text style={{ color: colors.ink[500], fontSize: 13, fontStyle: 'italic' }}>
                Aún sin ítems. Tocá «Agregar / editar» y empezá a armar la lista.
              </Text>
            )}
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
              <Button
                label="Agregar / editar"
                iconLeft={<FileEdit size={14} color={colors.white} />}
                onPress={onAbrirEditor}
              />
              <Button
                label="Enviar al admin"
                variant="success"
                iconLeft={<Send size={14} color={colors.white} />}
                onPress={onEnviar}
                disabled={borrador.items.length === 0}
              />
            </View>
          </>
        ) : (
          <>
            <Text style={{ color: colors.ink[500], fontSize: 13 }}>
              Esta área no tiene una lista en preparación. Creá una y vas agregando ítems durante los días que necesites.
            </Text>
            <Button label="Empezar lista" iconLeft={<Plus size={14} color={colors.white} />} onPress={onCrearBorrador} />
          </>
        )}
      </Card>

      {/* Stats */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        <StatTile label="Pendientes" value={String(pendientes)} accent="warning" />
        <StatTile label="En proceso" value={String(enProceso)} accent="magenta" />
        <StatTile label="Entregadas" value={String(entregadas)} accent="success" />
      </View>

      {/* Histórico */}
      <Card>
        <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
          Pedidos enviados de {nombreDeArea(areaId)}
        </Text>
      </Card>

      {loading ? (
        <SkeletonRows rows={3} />
      ) : enviadas.length === 0 ? (
        <Card>
          <EmptyState
            illustration="note"
            title="Sin pedidos enviados"
            message="Cuando envíes la lista al admin se mostrará el historial acá."
          />
        </Card>
      ) : (
        <View style={{ gap: 8 }}>
          {enviadas.map((s) => (
            <SolicitudCard key={s.id} solicitud={s} esAdmin={false} />
          ))}
        </View>
      )}

      <EditorBorradorSheet
        visible={editorOpen}
        onClose={() => setEditorOpen(false)}
        onCancelar={onCancelarEditor}
        borrador={borrador}
        area={areaId}
        areaNombreTxt={nombreDeArea(areaId)}
        tipo={tipo}
      />
    </ScrollView>
  );
}

// ============================================================================
// SolicitudCard — compacta, usada en admin y doctora
// ============================================================================
function SolicitudCard({ solicitud, esAdmin }: { solicitud: Solicitud; esAdmin: boolean }) {
  const [entregarOpen, setEntregarOpen] = useState(false);
  return (
    <View
      style={{
        padding: 16,
        backgroundColor: colors.white,
        borderRadius: radius['2xl'],
        borderWidth: 1,
        borderColor: colors.ink[100],
        gap: 12,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
        <View style={{ flex: 1, gap: 4 }}>
          <Text style={{ fontWeight: typography.weights.bold, color: colors.ink[900], fontSize: 15 }}>
            {solicitud.areaNombre ?? areaNombre(solicitud.area ?? '')}
          </Text>
          <Text style={{ color: colors.ink[500], fontSize: 12 }}>
            {formatDateTime(solicitud.enviadaEn?.toDate?.() ?? solicitud.creadaEn?.toDate?.() ?? null)} · Pedido por {solicitud.doctorNombre ?? solicitud.ultimoEditorNombre ?? 'desconocido'}
          </Text>
          {solicitud.estado === 'entregada' || solicitud.estado === 'entregada_parcial' ? (
            <Text style={{ color: colors.success[600], fontSize: 12, fontWeight: typography.weights.bold }}>
              {solicitud.estado === 'entregada' ? '✓ Entregado el ' : '✓ Última entrega: '}
              {formatDateTime(solicitud.entregadaEn?.toDate?.() ?? (solicitud as any).actualizadaEn?.toDate?.() ?? null)}
            </Text>
          ) : null}
        </View>
        <Badge label={ESTADO_LABEL[solicitud.estado]} tone={ESTADO_TONO[solicitud.estado]} />
      </View>

      <View style={{ gap: 6 }}>
        {solicitud.items.map((it, i) => {
          const entregado = it.cantidadEntregada ?? 0;
          const completo = entregado >= it.cantidad;
          const parcial = entregado > 0 && entregado < it.cantidad;
          return (
            <View
              key={i}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 10,
                borderRadius: 10,
                backgroundColor: completo
                  ? colors.success[50]
                  : parcial
                    ? colors.warning[50]
                    : colors.crema[50],
              }}
            >
              <Text style={{ color: colors.ink[800], fontSize: 13, flex: 1 }} numberOfLines={1}>
                {it.insumoNombre}
              </Text>
              <Text
                style={{
                  color: completo
                    ? colors.success[600]
                    : parcial
                      ? colors.warning[600]
                      : colors.ink[600],
                  fontSize: 13,
                  fontWeight: typography.weights.bold,
                }}
              >
                {entregado > 0 ? `${entregado}/${it.cantidad}` : `×${it.cantidad}`}
              </Text>
            </View>
          );
        })}
      </View>

      {solicitud.nota ? (
        <Text style={{ color: colors.ink[500], fontSize: 13, fontStyle: 'italic' }}>
          “{solicitud.nota}”
        </Text>
      ) : null}

      {esAdmin ? (
        <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {solicitud.estado === 'pendiente' ? (
            <>
              <Button
                label="Rechazar"
                variant="ghost"
                size="sm"
                iconLeft={<X size={14} color={colors.danger[600]} />}
                onPress={() => actualizarEstadoSolicitud(solicitud.id, 'rechazada')}
              />
              <Button
                label="Aprobar"
                size="sm"
                variant="secondary"
                iconLeft={<Check size={14} color={colors.magenta[700]} />}
                onPress={() => actualizarEstadoSolicitud(solicitud.id, 'aprobada')}
              />
              <Button
                label="Entregar directo"
                size="sm"
                variant="success"
                iconLeft={<PackageCheck size={14} color={colors.white} />}
                onPress={() => setEntregarOpen(true)}
              />
            </>
          ) : null}
          {solicitud.estado === 'aprobada' ? (
            <Button
              label="Marcar comprada"
              size="sm"
              variant="secondary"
              onPress={() => actualizarEstadoSolicitud(solicitud.id, 'comprada')}
            />
          ) : null}
          {(solicitud.estado === 'comprada' || solicitud.estado === 'entregada_parcial') ? (
            <Button
              label={solicitud.estado === 'entregada_parcial' ? 'Entregar más' : 'Entregar'}
              size="sm"
              variant="success"
              iconLeft={<PackageCheck size={14} color={colors.white} />}
              onPress={() => setEntregarOpen(true)}
            />
          ) : null}
        </View>
      ) : null}

      {esAdmin ? (
        <EntregarSheet
          visible={entregarOpen}
          onClose={() => setEntregarOpen(false)}
          solicitud={solicitud}
        />
      ) : null}
    </View>
  );
}

// ============================================================================
// Editor del borrador — agregar/quitar ítems
// ============================================================================
function EditorBorradorSheet({
  visible,
  onClose,
  onCancelar,
  borrador,
  area,
  areaNombreTxt,
  tipo = 'insumos',
}: {
  visible: boolean;
  onClose: () => void;
  onCancelar: () => void;
  borrador: Solicitud | null;
  area: string;
  areaNombreTxt: string;
  tipo?: TipoSolicitud;
}) {
  const perfil = useAuth((s) => s.perfil);
  const { data: insumos } = useFirestoreCollection(
    tipo === 'farmacia' ? productosVentaQuery : insumosInternosQuery,
  );

  const [items, setItems] = useState<ItemSolicitud[]>([]);
  const [nota, setNota] = useState('');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // OJO: dependemos de borrador.id (NO del objeto borrador) a propósito.
  // En modo API los datos se refrescan por polling cada 5s y crean un objeto
  // borrador nuevo; si dependiéramos de `borrador`, el efecto se redispararía y
  // borraría los ítems que la doctora va agregando antes de guardar. Solo
  // recargamos al abrir el modal o si cambia de borrador.
  useEffect(() => {
    if (!visible) return;
    setItems(borrador?.items ?? []);
    setNota(borrador?.nota ?? '');
    setSearch('');
    setError(null);
    setSaving(false);
    setCreandoNuevo(false);
    setNuevoNombre('');
    setNuevoPresentacion('');
    setGuardandoNuevo(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, borrador?.id]);

  const insumosFiltrados = useMemo(() => {
    const q = normalizar(search);
    if (!q) return insumos.slice().sort((a, b) => a.nombre.localeCompare(b.nombre));
    return insumos
      .filter(
        (i) =>
          normalizar(i.nombre).includes(q) ||
          normalizar(i.presentacion).includes(q) ||
          normalizar((i as any).categoria).includes(q),
      )
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [insumos, search]);

  const agregarItem = (insumoId: string) => {
    const insumo = insumos.find((i) => i.id === insumoId);
    if (!insumo) return;
    setItems((prev) => {
      const existe = prev.find((p) => p.insumoId === insumoId);
      if (existe) {
        return prev.map((p) =>
          p.insumoId === insumoId ? { ...p, cantidad: p.cantidad + 1 } : p,
        );
      }
      return [
        ...prev,
        {
          insumoId: insumo.id,
          insumoNombre: `${insumo.nombre} (${insumo.presentacion})`,
          cantidad: 1,
        },
      ];
    });
    setError(null);
  };

  const cambiarCantidad = (insumoId: string, delta: number) => {
    setItems((prev) =>
      prev
        .map((p) => (p.insumoId === insumoId ? { ...p, cantidad: p.cantidad + delta } : p))
        .filter((p) => p.cantidad > 0),
    );
  };

  const removeItem = (id: string) => setItems((prev) => prev.filter((p) => p.insumoId !== id));

  // La doctora puede crear un insumo nuevo en el catálogo (queda para todas) y
  // sumarlo a su pedido. Mini-formulario: nombre + presentación.
  const [creandoNuevo, setCreandoNuevo] = useState(false);
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoPresentacion, setNuevoPresentacion] = useState('');
  const [guardandoNuevo, setGuardandoNuevo] = useState(false);

  const abrirNuevo = (nombre: string) => {
    setNuevoNombre(nombre.trim());
    setNuevoPresentacion('');
    setCreandoNuevo(true);
    setError(null);
  };

  const crearNuevoInsumo = async () => {
    const nom = nuevoNombre.trim();
    if (!nom) {
      setError('Poné un nombre.');
      return;
    }
    const pres = nuevoPresentacion.trim() || 'Unidad';
    setGuardandoNuevo(true);
    setError(null);
    try {
      const id =
        tipo === 'farmacia'
          ? await upsertProductoVenta({ nombre: nom, presentacion: pres, stock: 0, stockMin: 0, precioCompra: 0, precioVenta: 0 })
          : await upsertInsumoInterno({ nombre: nom, presentacion: pres, stock: 0, stockMin: 0 });
      setItems((prev) => {
        if (prev.find((p) => p.insumoId === id)) return prev;
        return [...prev, { insumoId: id, insumoNombre: `${nom} (${pres})`, cantidad: 1 }];
      });
      setCreandoNuevo(false);
      setSearch('');
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo crear el insumo.');
    } finally {
      setGuardandoNuevo(false);
    }
  };

  // ¿Lo que escribió ya existe tal cual como insumo? (para no ofrecer "nuevo" al pedo)
  const hayCoincidenciaExacta = useMemo(() => {
    const q = normalizar(search);
    if (!q) return false;
    return insumos.some(
      (i) => normalizar(i.nombre) === q || normalizar(`${i.nombre} (${i.presentacion})`) === q,
    );
  }, [search, insumos]);

  // Existentes PARECIDOS al nombre que se quiere crear (comparten alguna palabra ≥3
  // letras). Sirve para avisar antes de crear un duplicado (ej. "Dexametasona").
  const parecidos = useMemo(() => {
    const q = normalizar(nuevoNombre);
    const palabras = q.split(/\s+/).filter((w) => w.length >= 3);
    if (palabras.length === 0) return [];
    return insumos.filter((i) => {
      const n = normalizar(i.nombre);
      return palabras.some((w) => n.includes(w));
    }).slice(0, 6);
  }, [nuevoNombre, insumos]);

  const onSave = async () => {
    if (!perfil) return;
    // Lista nueva sin ítems: no se crea nada (no deja "lista en preparación" fantasma).
    if (!borrador && items.length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      if (borrador) {
        await editarBorrador({
          id: borrador.id,
          items,
          nota: nota.trim() || null,
          ultimoEditorUid: perfil.uid,
          ultimoEditorNombre: perfil.nombre,
        });
      } else {
        await crearBorrador({
          area,
          areaNombre: areaNombreTxt,
          tipo,
          autorUid: perfil.uid,
          autorNombre: perfil.nombre,
          items,
          nota: nota.trim() || undefined,
        });
      }
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  // Guarda lo que está en pantalla y lo envía al admin en un solo paso.
  const onEnviar = async () => {
    if (!perfil) return;
    if (items.length === 0) {
      setError('Agregá al menos un ítem antes de enviar.');
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      let id = borrador?.id;
      if (id) {
        await editarBorrador({
          id,
          items,
          nota: nota.trim() || null,
          ultimoEditorUid: perfil.uid,
          ultimoEditorNombre: perfil.nombre,
        });
      } else {
        id = await crearBorrador({
          area,
          areaNombre: areaNombreTxt,
          tipo,
          autorUid: perfil.uid,
          autorNombre: perfil.nombre,
          items,
          nota: nota.trim() || undefined,
        });
      }
      await enviarBorrador(id, perfil.uid, perfil.nombre);
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo enviar.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Sheet
      visible={visible}
      onClose={onCancelar}
      size="md"
      title={tipo === 'farmacia' ? 'Pedido para Farmacia' : `Lista de ${areaNombreTxt}`}
      subtitle={tipo === 'farmacia' ? 'Lo que necesitás reponer en la farmacia. Después lo envía al admin.' : 'Vas agregando ítems hasta tener la lista completa. Después la enviás al admin.'}
      footer={
        <>
          <Button label="Cancelar" variant="ghost" onPress={onCancelar} disabled={saving || enviando} />
          <Button label="Guardar cambios" onPress={onSave} loading={saving} disabled={enviando} />
          <Button
            label="Enviar al admin"
            variant="success"
            iconLeft={<Send size={14} color={colors.white} />}
            onPress={onEnviar}
            loading={enviando}
            disabled={items.length === 0 || saving}
          />
        </>
      }
    >
      {/* Items elegidos */}
      {items.length > 0 ? (
        <View style={{ gap: 6 }}>
          <Text style={{ fontSize: 11, color: colors.ink[500], fontWeight: typography.weights.bold, letterSpacing: 1.4, textTransform: 'uppercase' }}>
            Lista actual · {items.length} {items.length === 1 ? 'ítem' : 'ítems'}
          </Text>
          {items.map((it) => (
            <View
              key={it.insumoId}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                padding: 10,
                borderRadius: 10,
                backgroundColor: colors.magenta[50],
                gap: 10,
              }}
            >
              <Text style={{ flex: 1, color: colors.magenta[800], fontWeight: typography.weights.semibold, fontSize: 13 }} numberOfLines={1}>
                {it.insumoNombre}
              </Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                <Pressable
                  onPress={() => cambiarCantidad(it.insumoId, -1)}
                  style={({ hovered }) => ({
                    width: 26,
                    height: 26,
                    borderRadius: 7,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: hovered ? colors.magenta[200] : colors.white,
                    borderWidth: 1,
                    borderColor: colors.magenta[200],
                    // @ts-ignore web only
                    cursor: 'pointer',
                  })}
                >
                  <Text style={{ color: colors.magenta[700], fontSize: 14, fontWeight: typography.weights.bold }}>−</Text>
                </Pressable>
                <Text style={{ width: 28, textAlign: 'center', color: colors.magenta[800], fontWeight: typography.weights.bold }}>
                  {it.cantidad}
                </Text>
                <Pressable
                  onPress={() => cambiarCantidad(it.insumoId, +1)}
                  style={({ hovered }) => ({
                    width: 26,
                    height: 26,
                    borderRadius: 7,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: hovered ? colors.magenta[700] : colors.magenta[600],
                    // @ts-ignore web only
                    cursor: 'pointer',
                  })}
                >
                  <Text style={{ color: colors.white, fontSize: 14, fontWeight: typography.weights.bold }}>+</Text>
                </Pressable>
              </View>
              <Pressable onPress={() => cambiarCantidad(it.insumoId, -it.cantidad)} hitSlop={6}>
                <Trash2 size={14} color={colors.danger[600]} />
              </Pressable>
            </View>
          ))}
        </View>
      ) : null}

      {/* Buscador */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
          backgroundColor: colors.crema[50],
          borderRadius: radius.full,
          paddingHorizontal: 14,
          height: 44,
          borderWidth: 1,
          borderColor: colors.ink[100],
        }}
      >
        <Text style={{ fontSize: 14, color: colors.ink[400] }}>🔍</Text>
        {/* @ts-ignore web-only native input */}
        <input
          type="text"
          placeholder={tipo === 'farmacia' ? 'Buscar o escribir un producto…' : 'Buscar o escribir un insumo nuevo…'}
          value={search}
          onChange={(e: any) => setSearch(e.target.value)}
          onKeyDown={(e: any) => {
            if (e.key === 'Enter' && search.trim() && !hayCoincidenciaExacta && !creandoNuevo) abrirNuevo(search);
          }}
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            fontSize: 14,
            color: colors.ink[900],
            fontFamily: 'inherit',
          }}
        />
      </View>

      {/* Crear un insumo nuevo en el catálogo (queda para todas) */}
      {creandoNuevo ? (
        <View style={{ gap: 10, padding: 14, borderRadius: radius.xl, borderWidth: 1.5, borderColor: colors.magenta[300], backgroundColor: colors.magenta[50] }}>
          <Text style={{ fontSize: 13, fontWeight: typography.weights.bold, color: colors.magenta[700] }}>
            Nuevo {tipo === 'farmacia' ? 'producto' : 'insumo'} (se guarda para todas)
          </Text>

          {/* Aviso de parecidos: evita crear duplicados (ej. "Dexametasona") */}
          {parecidos.length > 0 ? (
            <View style={{ gap: 6, padding: 10, borderRadius: 12, backgroundColor: colors.warning[50], borderWidth: 1, borderColor: colors.warning[500] }}>
              <Text style={{ fontSize: 12, fontWeight: typography.weights.bold, color: colors.warning[600] }}>
                ⚠️ Ya existe(n) parecido(s). ¿No será alguno de estos?
              </Text>
              {parecidos.map((p) => (
                <Pressable
                  key={p.id}
                  onPress={() => { agregarItem(p.id); setCreandoNuevo(false); setSearch(''); setNuevoNombre(''); }}
                  style={({ hovered }) => ({
                    flexDirection: 'row', alignItems: 'center', gap: 8,
                    paddingHorizontal: 10, paddingVertical: 8, borderRadius: 9,
                    backgroundColor: hovered ? colors.white : 'rgba(255,255,255,0.6)',
                    borderWidth: 1, borderColor: colors.warning[500],
                    // @ts-ignore web only
                    cursor: 'pointer',
                  })}
                >
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: colors.ink[900] }} numberOfLines={1}>{p.nombre}</Text>
                    <Text style={{ fontSize: 11, color: colors.ink[500] }}>{p.presentacion} · stock {p.stock}</Text>
                  </View>
                  <Text style={{ fontSize: 12, fontWeight: typography.weights.bold, color: colors.magenta[700] }}>Usar este →</Text>
                </Pressable>
              ))}
              <Text style={{ fontSize: 11, color: colors.ink[500] }}>Creá uno nuevo solo si de verdad es distinto a estos.</Text>
            </View>
          ) : null}

          <TextField label="Nombre" value={nuevoNombre} onChangeText={setNuevoNombre} placeholder="Ej: Jeringa 5 ml" />
          <TextField label="Presentación" value={nuevoPresentacion} onChangeText={setNuevoPresentacion} placeholder="Caja, Unidad, Frasco…" />
          <Text style={{ fontSize: 11.5, color: colors.ink[500] }}>
            Entra con stock 0. Administración lo repone cuando lo consiga.
          </Text>
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button label="Cancelar" variant="ghost" size="sm" onPress={() => setCreandoNuevo(false)} disabled={guardandoNuevo} />
            <Button label="Crear y agregar" size="sm" onPress={crearNuevoInsumo} loading={guardandoNuevo} />
          </View>
        </View>
      ) : search.trim() && !hayCoincidenciaExacta ? (
        <Pressable
          onPress={() => abrirNuevo(search)}
          style={({ hovered }) => ({
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
            paddingHorizontal: 14,
            paddingVertical: 12,
            borderRadius: radius.xl,
            borderWidth: 1.5,
            borderStyle: 'dashed',
            borderColor: colors.magenta[300],
            backgroundColor: hovered ? colors.magenta[50] : colors.white,
            // @ts-ignore web only
            cursor: 'pointer',
          })}
        >
          <View style={{ width: 28, height: 28, borderRadius: 8, backgroundColor: colors.magenta[600], alignItems: 'center', justifyContent: 'center' }}>
            <Plus size={16} color={colors.white} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 13.5, fontWeight: typography.weights.bold, color: colors.magenta[700] }} numberOfLines={1}>
              Crear «{search.trim()}» como {tipo === 'farmacia' ? 'producto' : 'insumo'} nuevo
            </Text>
            <Text style={{ fontSize: 11.5, color: insumosFiltrados.length > 0 ? colors.warning[600] : colors.ink[500] }}>
              {insumosFiltrados.length > 0
                ? '⚠️ Mirá primero la lista de abajo, capaz ya existe. Recién si no está, creá uno nuevo.'
                : 'No está en el catálogo. Lo creás y queda para todas.'}
            </Text>
          </View>
        </Pressable>
      ) : null}

      {/* Lista de productos disponibles */}
      <View
        style={{
          maxHeight: 260,
          borderRadius: radius['2xl'],
          borderWidth: 1,
          borderColor: colors.ink[100],
          backgroundColor: colors.white,
          overflow: 'hidden',
        }}
      >
        <ScrollView style={{ maxHeight: 260 }}>
          {insumosFiltrados.length === 0 ? (
            <View style={{ padding: 18, alignItems: 'center', gap: 2 }}>
              <Text style={{ color: colors.ink[500], fontSize: 13 }}>
                No está en el catálogo.
              </Text>
              <Text style={{ color: colors.ink[400], fontSize: 12 }}>
                Tocá «Agregar» de arriba para pedirlo igual.
              </Text>
            </View>
          ) : (
            insumosFiltrados.map((p, idx) => {
              const enLista = items.find((it) => it.insumoId === p.id);
              return (
                <Pressable
                  key={p.id}
                  onPress={() => agregarItem(p.id)}
                  style={({ hovered }) => ({
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 10,
                    paddingHorizontal: 14,
                    paddingVertical: 10,
                    backgroundColor: hovered ? colors.crema[50] : 'transparent',
                    borderBottomWidth: idx === insumosFiltrados.length - 1 ? 0 : 1,
                    borderBottomColor: colors.ink[100],
                    // @ts-ignore web only
                    cursor: 'pointer',
                  })}
                >
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 14, fontWeight: typography.weights.bold, color: colors.ink[900] }} numberOfLines={1}>
                      {p.nombre}
                    </Text>
                    <Text style={{ fontSize: 11, color: colors.ink[500], marginTop: 2 }}>
                      {p.presentacion} · stock {p.stock}
                    </Text>
                  </View>
                  <View
                    style={{
                      paddingHorizontal: 10,
                      height: 30,
                      borderRadius: 8,
                      backgroundColor: enLista ? colors.success[500] : colors.magenta[100],
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <Text style={{ color: enLista ? colors.white : colors.magenta[700], fontSize: 16, fontWeight: typography.weights.bold }}>+</Text>
                    {enLista ? (
                      <Text style={{ color: colors.white, fontSize: 12, fontWeight: typography.weights.bold }}>
                        {enLista.cantidad}
                      </Text>
                    ) : null}
                  </View>
                </Pressable>
              );
            })
          )}
        </ScrollView>
      </View>

      <TextField label="Nota (opcional)" placeholder="Para qué los necesitan, urgencia, etc." value={nota} onChangeText={setNota} multiline />

      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{error}</Text>
        </View>
      ) : null}
    </Sheet>
  );
}

// ============================================================================
// EntregarSheet — admin marca cuánto se entrega de cada item (entrega total o parcial)
// ============================================================================
function EntregarSheet({
  visible,
  onClose,
  solicitud,
}: {
  visible: boolean;
  onClose: () => void;
  solicitud: Solicitud;
}) {
  // Por cada item: cuánto entregamos esta vez (default = restante)
  const [entregas, setEntregas] = useState<Record<number, number>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    const init: Record<number, number> = {};
    solicitud.items.forEach((it, i) => {
      const restante = it.cantidad - (it.cantidadEntregada ?? 0);
      init[i] = restante > 0 ? restante : 0;
    });
    setEntregas(init);
    setError(null);
    setSaving(false);
    // OJO: dependemos de solicitud.id (NO del objeto). En modo API los datos se
    // refrescan por polling cada 5s creando un objeto nuevo; si dependiéramos de
    // `solicitud`, el efecto se redispararía y borraría las cantidades que la
    // admin va ajustando. Solo re-inicializamos al abrir o al cambiar de solicitud.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, solicitud.id]);

  const totalAEntregar = Object.values(entregas).reduce((a, b) => a + b, 0);

  const cambiar = (idx: number, delta: number, max: number) => {
    setEntregas((prev) => {
      const next = Math.max(0, Math.min(max, (prev[idx] ?? 0) + delta));
      return { ...prev, [idx]: next };
    });
  };

  const setExacto = (idx: number, v: string, max: number) => {
    const n = Number(v);
    if (Number.isNaN(n)) return;
    setEntregas((prev) => ({ ...prev, [idx]: Math.max(0, Math.min(max, n)) }));
  };

  const onConfirmar = async () => {
    if (totalAEntregar === 0) return setError('Marcá al menos un ítem para entregar.');
    setSaving(true);
    setError(null);
    try {
      await entregarSolicitud(solicitud.id, entregas);
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo registrar la entrega.');
    } finally {
      setSaving(false);
    }
  };

  // Después de aplicar, ¿queda algo pendiente?
  const quedaPendiente = solicitud.items.some((it, i) => {
    const yaEntregado = it.cantidadEntregada ?? 0;
    const ahora = entregas[i] ?? 0;
    return yaEntregado + ahora < it.cantidad;
  });

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      size="md"
      title="Registrar entrega"
      subtitle={`Solicitud de ${solicitud.areaNombre ?? areaNombre(solicitud.area ?? '')}`}
      footer={
        <>
          <Button label="Cancelar" variant="ghost" onPress={onClose} />
          <Button
            label={quedaPendiente ? 'Entregar parcial' : 'Entregar todo'}
            iconLeft={<PackageCheck size={14} color={colors.white} />}
            onPress={onConfirmar}
            loading={saving}
            disabled={totalAEntregar === 0}
            variant="success"
          />
        </>
      }
    >
      <Text style={{ fontSize: 13, color: colors.ink[600] }}>
        Ajustá la cantidad a entregar de cada ítem. Los que no entregues quedan pendientes y la solicitud se marca como parcial.
      </Text>

      <View style={{ gap: 8 }}>
        {solicitud.items.map((it, i) => {
          const yaEntregado = it.cantidadEntregada ?? 0;
          const restante = it.cantidad - yaEntregado;
          const valor = entregas[i] ?? 0;
          const trasEntrega = yaEntregado + valor;
          return (
            <View
              key={i}
              style={{
                padding: 12,
                borderRadius: radius.xl,
                backgroundColor: colors.crema[50],
                gap: 8,
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <Text style={{ flex: 1, fontSize: 13, fontWeight: typography.weights.bold, color: colors.ink[900] }} numberOfLines={2}>
                  {it.insumoNombre}
                </Text>
                <Text style={{ fontSize: 12, color: colors.ink[500] }}>
                  pedidos {it.cantidad} · ya entregados {yaEntregado}
                </Text>
              </View>

              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Pressable
                  onPress={() => cambiar(i, -1, restante)}
                  disabled={restante === 0}
                  style={({ hovered }) => ({
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: restante === 0 ? colors.ink[100] : hovered ? colors.crema[100] : colors.white,
                    borderWidth: 1,
                    borderColor: colors.ink[200],
                    opacity: restante === 0 ? 0.5 : 1,
                    // @ts-ignore web only
                    cursor: restante === 0 ? 'not-allowed' : 'pointer',
                  })}
                >
                  <Text style={{ fontSize: 18, color: colors.ink[700], fontWeight: typography.weights.bold }}>−</Text>
                </Pressable>

                <TextField
                  containerStyle={{ flex: 1 }}
                  value={String(valor)}
                  onChangeText={(t) => setExacto(i, t, restante)}
                  keyboardType="numeric"
                />

                <Pressable
                  onPress={() => cambiar(i, +1, restante)}
                  disabled={restante === 0}
                  style={({ hovered }) => ({
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: restante === 0 ? colors.ink[100] : hovered ? colors.magenta[200] : colors.magenta[100],
                    opacity: restante === 0 ? 0.5 : 1,
                    // @ts-ignore web only
                    cursor: restante === 0 ? 'not-allowed' : 'pointer',
                  })}
                >
                  <Text style={{ fontSize: 18, color: colors.magenta[700], fontWeight: typography.weights.bold }}>+</Text>
                </Pressable>

                <Text style={{ width: 80, textAlign: 'right', fontSize: 12, color: colors.ink[600] }}>
                  Quedará {trasEntrega}/{it.cantidad}
                </Text>
              </View>
            </View>
          );
        })}
      </View>

      <View
        style={{
          padding: 14,
          borderRadius: radius.xl,
          backgroundColor: quedaPendiente ? colors.warning[50] : colors.success[50],
          borderWidth: 1,
          borderColor: quedaPendiente ? colors.warning[500] : colors.success[500],
        }}
      >
        <Text style={{ fontSize: 13, fontWeight: typography.weights.bold, color: quedaPendiente ? colors.warning[600] : colors.success[600] }}>
          {quedaPendiente ? 'Entrega parcial' : 'Entrega completa'}
        </Text>
        <Text style={{ fontSize: 12, color: colors.ink[600], marginTop: 2 }}>
          {quedaPendiente
            ? 'La solicitud queda marcada como parcial. Cuando consigas el resto, abrila y entregá más.'
            : 'Se descuenta del stock y la solicitud queda como entregada.'}
        </Text>
      </View>

      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{error}</Text>
        </View>
      ) : null}
    </Sheet>
  );
}
